import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DescricaoPrioridade, PrioridadeEnum } from 'src/app/enum/prioridade.enum';
import { DescricaoStatusTicket, StatusTicketEnum } from 'src/app/enum/statusTicket.enum';
import { CorTipoTicket, DescricaoTipoTicket, TipoTicketEnum } from 'src/app/enum/tipoTicket.enum';
import { SegmentoService } from 'src/app/service/segmento.service';
import { TicketService } from 'src/app/service/ticket.service';
import { NotaTicket } from './nota';
import { Segmento } from '../segmento/segmento';
import { Ticket } from './ticket';

interface ColunaKanban {
  id: string;
  titulo: string;
  statuses: StatusTicketEnum[];
  statusGravacao: StatusTicketEnum;
  tickets: Ticket[];
  limiteWip?: number;
}

interface MovimentoPendente {
  ticket: Ticket;
  statusAnterior: StatusTicketEnum;
  statusDestino: StatusTicketEnum;
  labelDestino: string;
  colunaOrigemId: string;
  colunaDestinoId: string;
  indiceDestino: number;
  escolherStatusConcluido: boolean;
}

interface ResumoKanban {
  abertos: number;
  atrasados: number;
  concluidosSemana: number;
}

@Component({
  selector: 'app-ticket-kanban',
  templateUrl: './ticket-kanban.component.html',
  styleUrls: [
    './ticket-kanban.component.scss',
    '../../util/conta-financeira/conta-financeira-consulta.scss',
  ],
})
export class TicketKanbanComponent implements OnInit, OnDestroy {

  @ViewChild('dialogMotivo') dialogMotivo!: TemplateRef<any>;
  @ViewChild('board') boardRef!: ElementRef<HTMLElement>;

  private readonly WIP_DESENVOLVIMENTO = 5;
  private readonly POLLING_MS = 45000;

  private readonly statusConcluido: StatusTicketEnum[] = [
    StatusTicketEnum.AGUARDANDO_HOMOLOGACAO_CLIENTE,
    StatusTicketEnum.RESOLVIDO,
    StatusTicketEnum.FINALIZADO_CLIENTE,
    StatusTicketEnum.FINALIZADO_AUTOMATICO,
  ];

  readonly opcoesStatusConcluido = this.statusConcluido;

  private readonly definicaoColunas: Array<{
    id: string;
    titulo: string;
    statuses: StatusTicketEnum[];
    statusGravacao: StatusTicketEnum;
    limiteWip?: number;
  }> = [
    {
      id: 'BACKLOG',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.BACKLOG) || 'Backlog',
      statuses: [StatusTicketEnum.BACKLOG],
      statusGravacao: StatusTicketEnum.BACKLOG,
    },
    {
      id: 'EM_ANALISE',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.EM_ANALISE) || 'Em Análise',
      statuses: [StatusTicketEnum.EM_ANALISE],
      statusGravacao: StatusTicketEnum.EM_ANALISE,
    },
    {
      id: 'EM_DESENVOLVIMENTO',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.EM_DESENVOLVIMENTO) || 'Em Desenvolvimento',
      statuses: [StatusTicketEnum.EM_DESENVOLVIMENTO],
      statusGravacao: StatusTicketEnum.EM_DESENVOLVIMENTO,
      limiteWip: this.WIP_DESENVOLVIMENTO,
    },
    {
      id: 'EM_HOMOLOGACAO',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.EM_HOMOLOGACAO) || 'Em Homologação',
      statuses: [StatusTicketEnum.EM_HOMOLOGACAO],
      statusGravacao: StatusTicketEnum.EM_HOMOLOGACAO,
    },
    {
      id: 'AGUARNDANDO_CLIENTE',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.AGUARNDANDO_CLIENTE) || 'Aguardando Cliente',
      statuses: [StatusTicketEnum.AGUARNDANDO_CLIENTE],
      statusGravacao: StatusTicketEnum.AGUARNDANDO_CLIENTE,
    },
    {
      id: 'AGUARNDANDO_MERGE',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.AGUARNDANDO_MERGE) || 'Aguardando Merge',
      statuses: [StatusTicketEnum.AGUARNDANDO_MERGE],
      statusGravacao: StatusTicketEnum.AGUARNDANDO_MERGE,
    },
    {
      id: 'AGUARDANDO_ATUALIZACAO_SISTEMA',
      titulo: DescricaoStatusTicket.get(StatusTicketEnum.AGUARDANDO_ATUALIZACAO_SISTEMA)
        || 'Aguardando Atualização do Sistema',
      statuses: [StatusTicketEnum.AGUARDANDO_ATUALIZACAO_SISTEMA],
      statusGravacao: StatusTicketEnum.AGUARDANDO_ATUALIZACAO_SISTEMA,
    },
    {
      id: 'CONCLUIDO',
      titulo: 'Concluído',
      statuses: this.statusConcluido,
      statusGravacao: StatusTicketEnum.RESOLVIDO,
    },
  ];

  colunas: ColunaKanban[] = [];
  segmentos: Segmento[] = [];
  todosTickets: Ticket[] = [];
  resumo: ResumoKanban = { abertos: 0, atrasados: 0, concluidosSemana: 0 };

  filtroMeusTickets = false;
  filtroSegmento: string | null = null;

  carregando = false;
  movendo = false;
  alteracaoMotivo = '';
  statusConcluidoEscolhido: StatusTicketEnum = StatusTicketEnum.RESOLVIDO;
  movimentoPendente: MovimentoPendente | null = null;

  private dialogRef: MatDialogRef<any> | null = null;
  private pollingId: any = null;
  private arrastando = false;
  private usuarioLogado = (localStorage.getItem('usuarioLogado') || '').trim().toUpperCase();

  constructor(
    private ticketService: TicketService,
    private segmentoService: SegmentoService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.montarColunasVazias();
    this.segmentoService.listarAtivos().subscribe(lista => {
      this.segmentos = lista ?? [];
    });
    this.carregar(true);
    this.pollingId = setInterval(() => this.carregar(false), this.POLLING_MS);
  }

  ngOnDestroy(): void {
    if (this.pollingId) {
      clearInterval(this.pollingId);
    }
  }

  carregar(mostrarLoading = true): void {
    if (this.movimentoPendente || this.movendo) {
      return;
    }
    if (mostrarLoading) {
      this.carregando = true;
    }
    this.ticketService.consultar({
      segmento: this.filtroSegmento,
    }, 0, 500).subscribe({
      next: (response) => {
        this.todosTickets = response.content ?? [];
        this.aplicarFiltrosEDistribuir();
        this.carregando = false;
      },
      error: () => {
        this.carregando = false;
        if (mostrarLoading) {
          this.snackBar.open('Erro ao carregar o Kanban.', 'ERRO', { duration: 2500 });
        }
      },
    });
  }

  aplicarFiltrosEDistribuir(): void {
    let lista = [...this.todosTickets];
    if (this.filtroMeusTickets) {
      lista = lista.filter(t => this.souResponsavel(t));
    }
    this.distribuirTickets(lista);
    this.atualizarResumo(lista);
  }

  onFiltroChange(): void {
    this.aplicarFiltrosEDistribuir();
  }

  drop(event: CdkDragDrop<Ticket[]>, colunaDestino: ColunaKanban): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    const ticket = event.previousContainer.data[event.previousIndex];
    if (!ticket?.id || this.movendo || this.movimentoPendente) {
      return;
    }

    const colunaOrigem = this.colunas.find(c => c.tickets === event.previousContainer.data);
    if (!colunaOrigem) {
      return;
    }

    if (colunaOrigem.id === colunaDestino.id) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      return;
    }

    if (colunaDestino.limiteWip != null && colunaDestino.tickets.length >= colunaDestino.limiteWip) {
      this.snackBar.open(
        `Atenção: ${colunaDestino.titulo} já tem ${colunaDestino.tickets.length} tickets (limite ${colunaDestino.limiteWip}).`,
        'WIP',
        { duration: 3500 },
      );
    }

    const statusAnterior = ticket.status;
    const escolherStatusConcluido = colunaDestino.id === 'CONCLUIDO';
    const statusDestino = escolherStatusConcluido
      ? StatusTicketEnum.RESOLVIDO
      : colunaDestino.statusGravacao;

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex,
    );
    ticket.status = statusDestino;
    this.statusConcluidoEscolhido = StatusTicketEnum.RESOLVIDO;

    this.movimentoPendente = {
      ticket,
      statusAnterior,
      statusDestino,
      labelDestino: colunaDestino.titulo,
      colunaOrigemId: colunaOrigem.id,
      colunaDestinoId: colunaDestino.id,
      indiceDestino: event.currentIndex,
      escolherStatusConcluido,
    };
    this.alteracaoMotivo = '';
    this.scrollParaColuna(colunaDestino.id);

    this.dialogRef = this.dialog.open(this.dialogMotivo, {
      width: '480px',
      disableClose: true,
    });
  }

  confirmarMudancaStatus(): void {
    if (!this.movimentoPendente) {
      return;
    }
    if (!this.alteracaoMotivo?.trim()) {
      this.snackBar.open('Informe o motivo da alteração.', 'ATENÇÃO', { duration: 3000 });
      return;
    }

    if (this.movimentoPendente.escolherStatusConcluido) {
      this.movimentoPendente.statusDestino = this.statusConcluidoEscolhido;
      this.movimentoPendente.labelDestino =
        DescricaoStatusTicket.get(this.statusConcluidoEscolhido) || this.statusConcluidoEscolhido;
      this.movimentoPendente.ticket.status = this.statusConcluidoEscolhido;
    }

    const { ticket, statusAnterior, statusDestino, labelDestino, indiceDestino,
      colunaOrigemId, colunaDestinoId } = this.movimentoPendente;
    const anteriorLabel = DescricaoStatusTicket.get(statusAnterior) || statusAnterior;
    const texto = `🔧 Status alterado de "${anteriorLabel}" para "${labelDestino}".\nMotivo: ${this.alteracaoMotivo.trim()}`;

    this.movendo = true;
    this.ticketService.getById(ticket.id).subscribe({
      next: (completo) => {
        completo.status = statusDestino;
        this.ticketService.update(completo).subscribe({
          next: () => {
            const nota = new NotaTicket(
              0,
              texto,
              null,
              localStorage.getItem('usuarioLogado') || 'Sistema',
              ticket.id,
              false,
              [],
            );
            this.ticketService.adicionarComentario(ticket.id, nota, 0, 5).subscribe({
              next: () => {
                this.finalizarMovimentoOk(ticket.id, labelDestino);
                this.carregar(false);
              },
              error: () => {
                this.finalizarMovimentoOk(ticket.id, labelDestino);
                this.snackBar.open('Status atualizado, mas falhou ao registrar a observação.', 'ATENÇÃO', {
                  duration: 3500,
                });
                this.carregar(false);
              },
            });
          },
          error: () => {
            this.reverterMovimento(ticket, statusAnterior, colunaOrigemId, colunaDestinoId, indiceDestino);
            this.limparMovimento();
            this.snackBar.open('Não foi possível atualizar o status.', 'ERRO', { duration: 3000 });
          },
        });
      },
      error: () => {
        this.reverterMovimento(ticket, statusAnterior, colunaOrigemId, colunaDestinoId, indiceDestino);
        this.limparMovimento();
        this.snackBar.open('Não foi possível atualizar o status.', 'ERRO', { duration: 3000 });
      },
    });
  }

  cancelarMudancaStatus(): void {
    if (!this.movimentoPendente) {
      this.dialogRef?.close();
      return;
    }
    const { ticket, statusAnterior, colunaOrigemId, colunaDestinoId, indiceDestino } = this.movimentoPendente;
    this.reverterMovimento(ticket, statusAnterior, colunaOrigemId, colunaDestinoId, indiceDestino);
    this.limparMovimento();
  }

  onMotivoKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.confirmarMudancaStatus();
    }
  }

  onDragStarted(): void {
    this.arrastando = true;
  }

  onDragEnded(): void {
    setTimeout(() => { this.arrastando = false; }, 80);
  }

  onCardClick(ticket: Ticket): void {
    if (!this.arrastando) {
      this.abrirTicket(ticket);
    }
  }

  abrirTicket(ticket: Ticket): void {
    if (ticket?.id) {
      this.router.navigate(['/ticket/form', ticket.id]);
    }
  }

  ultrapassouWip(coluna: ColunaKanban): boolean {
    return coluna.limiteWip != null && coluna.tickets.length > coluna.limiteWip;
  }

  atingiuWip(coluna: ColunaKanban): boolean {
    return coluna.limiteWip != null && coluna.tickets.length >= coluna.limiteWip;
  }

  estaAtrasado(ticket: Ticket): boolean {
    if (this.statusConcluido.includes(ticket.status)) {
      return false;
    }
    const data = this.parseData(ticket.dataPrevisaoEntrega);
    if (!data) {
      return false;
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return data < hoje;
  }

  corCategoria(tipo: TipoTicketEnum | string): string {
    return CorTipoTicket.get(tipo as TipoTicketEnum) || '#9E9E9E';
  }

  corPrioridade(prioridade: PrioridadeEnum | string): string {
    switch (prioridade) {
      case PrioridadeEnum.ALTA:
      case 'ALTA':
        return '#C62828';
      case PrioridadeEnum.MEDIA:
      case 'MEDIA':
        return '#EF6C00';
      case PrioridadeEnum.BASICA:
      case 'BASICA':
        return '#546E7A';
      default:
        return '#9E9E9E';
    }
  }

  labelCategoria(tipo: TipoTicketEnum | string): string {
    return DescricaoTipoTicket.get(tipo as TipoTicketEnum) || String(tipo || '—');
  }

  labelPrioridade(prioridade: PrioridadeEnum | string): string {
    return DescricaoPrioridade.get(prioridade as PrioridadeEnum) || String(prioridade || '');
  }

  labelStatus(status: StatusTicketEnum | string): string {
    return DescricaoStatusTicket.get(status as StatusTicketEnum) || String(status || '');
  }

  labelSegmento(codigo?: string): string {
    if (!codigo) {
      return '—';
    }
    return this.segmentos.find(s => s.codigo === codigo)?.nome ?? codigo;
  }

  nomeResponsavel(ticket: Ticket): string {
    const dev = ticket.responsavelDesenvolvimento as any;
    const sup = ticket.responsavelSuporte as any;
    return dev?.name ?? sup?.name ?? '—';
  }

  iniciaisResponsavel(ticket: Ticket): string {
    const nome = this.nomeResponsavel(ticket);
    if (!nome || nome === '—') {
      return '?';
    }
    const partes = nome.trim().split(/\s+/).filter(Boolean);
    if (partes.length === 1) {
      return partes[0].substring(0, 2).toUpperCase();
    }
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  }

  nomeParceiro(ticket: Ticket): string {
    const p: any = ticket?.parceiro;
    if (!p) {
      return 'Interno';
    }
    return p.nomeFantasia || p.razaoSocial || 'Parceiro';
  }

  tempoGastoFormatado(ticket: Ticket): string {
    const total = ticket.tempoGastoSegundos || 0;
    if (!total) {
      return '0m';
    }
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    if (h > 0) {
      return `${h}h ${m}m`;
    }
    return `${m}m`;
  }

  private souResponsavel(ticket: Ticket): boolean {
    if (!this.usuarioLogado) {
      return false;
    }
    const candidatos = [
      ticket.responsavelSuporte as any,
      ticket.responsavelDesenvolvimento as any,
    ];
    return candidatos.some(u => {
      if (!u) {
        return false;
      }
      const username = String(u.username || '').toUpperCase();
      const name = String(u.name || '').toUpperCase();
      return username === this.usuarioLogado || name === this.usuarioLogado;
    });
  }

  private atualizarResumo(tickets: Ticket[]): void {
    const agora = new Date();
    const seteDiasAtras = new Date(agora);
    seteDiasAtras.setDate(agora.getDate() - 7);

    let abertos = 0;
    let atrasados = 0;
    let concluidosSemana = 0;

    for (const t of tickets) {
      const concluido = this.statusConcluido.includes(t.status);
      if (!concluido) {
        abertos++;
        if (this.estaAtrasado(t)) {
          atrasados++;
        }
      } else {
        const dataConclusao = this.parseData(t.dataFinalizacao)
          || this.parseData(t.datConclusaoEntrega)
          || this.parseData(t.dataConfirmacaoEntrega);
        if (dataConclusao && dataConclusao >= seteDiasAtras) {
          concluidosSemana++;
        }
      }
    }

    this.resumo = { abertos, atrasados, concluidosSemana };
  }

  private parseData(valor: any): Date | null {
    if (!valor) {
      return null;
    }
    if (valor instanceof Date) {
      return isNaN(valor.getTime()) ? null : valor;
    }
    if (typeof valor === 'string') {
      const br = valor.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (br) {
        return new Date(+br[3], +br[2] - 1, +br[1]);
      }
      const d = new Date(valor);
      return isNaN(d.getTime()) ? null : d;
    }
    if (Array.isArray(valor) && valor.length >= 3) {
      return new Date(valor[0], valor[1] - 1, valor[2]);
    }
    return null;
  }

  private scrollParaColuna(colunaId: string): void {
    setTimeout(() => {
      const board = this.boardRef?.nativeElement;
      const coluna = board?.querySelector(`[data-coluna-id="${colunaId}"]`) as HTMLElement;
      if (board && coluna) {
        const left = coluna.offsetLeft - 24;
        board.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }, 50);
  }

  private finalizarMovimentoOk(ticketId: number, novoLabel: string): void {
    this.limparMovimento();
    this.snackBar.open(`Ticket #${ticketId} → ${novoLabel}`, 'OK', { duration: 2000 });
  }

  private limparMovimento(): void {
    this.movendo = false;
    this.movimentoPendente = null;
    this.alteracaoMotivo = '';
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  private montarColunasVazias(): void {
    this.colunas = this.definicaoColunas.map(def => ({
      ...def,
      tickets: [],
    }));
  }

  private distribuirTickets(tickets: Ticket[]): void {
    this.montarColunasVazias();
    for (const ticket of tickets) {
      const coluna = this.colunas.find(c => c.statuses.includes(ticket.status));
      if (coluna) {
        coluna.tickets.push(ticket);
      } else if (this.colunas.length) {
        this.colunas[0].tickets.push(ticket);
      }
    }
  }

  private reverterMovimento(
    ticket: Ticket,
    statusAnterior: StatusTicketEnum,
    colunaOrigemId: string,
    colunaDestinoId: string,
    indiceAtual: number,
  ): void {
    const origem = this.colunas.find(c => c.id === colunaOrigemId);
    const destino = this.colunas.find(c => c.id === colunaDestinoId);
    if (!origem || !destino) {
      this.carregar(false);
      return;
    }
    const idx = destino.tickets.findIndex(t => t.id === ticket.id);
    if (idx >= 0) {
      transferArrayItem(destino.tickets, origem.tickets, idx, Math.min(indiceAtual, origem.tickets.length));
    }
    ticket.status = statusAnterior;
  }
}
